export enum ReporterSections {
  General = 'general',
  ECDN11 = 'ecdn11'
}

export const CONDITION_CRITICAL = {};

export const CONDITION_EXPEDITE = {
  CRITICAL: 'critical'
};

export type MetadataTag =
  | 'audio'
  | 'b'
  | 'caption'
  | 'cr'
  | 'dur'
  | 'foc'
  | 'full'
  | 'mute'
  | 'pip'
  | 'pos'
  | 'rate'
  | 'stop'
  | 'vis'
  | 'vr';

interface ProtoEvent {
  code: number;
  metadata?: MetadataTag;
}

export interface EventObject extends ProtoEvent {
  name: MonitoredEventList;
}

// Add name field on each value identical to its key
const addNameField = (
  events: Record<string, ProtoEvent>
): Readonly<Record<string, Readonly<EventObject>>> =>
  Object.freeze(
    Object.getOwnPropertyNames(events).reduce((result, n) => {
      const name = n as MonitoredEventList;
      result[name] = Object.freeze({ name, ...events[name] });
      return result;
    }, {} as Record<string, EventObject>)
  );

/**
 * The names of methods to get player event data from the player via the TechApi.
 */
export enum TechEngagementMethods {
  AUDIOCHANGED = 'audiochanged',
  BITRATECHANGED = 'bitrate',
  CAPTIONCHANGED = 'captionchanged'
}

/**
 * The list of HTMLVideoElement or HTMLMediaElement events we listen to
 */
export type MonitoredMediaEventList =
  | 'canplay'
  | 'canplaythrough'
  | 'complete'
  | 'durationchange'
  | 'emptied'
  | 'ended'
  | 'loadeddata'
  | 'loadedmetadata'
  | 'pause'
  | 'play'
  | 'playing'
  | 'ratechange'
  | 'resize'
  | 'seeked'
  | 'seeking'
  | 'stalled'
  | 'suspend'
  | 'volumechange'
  | 'waiting';

export type MonitoredMediaEventType = Readonly<
  Record<MonitoredMediaEventList, Readonly<EventObject>>
>;

export const MonitoredMediaEvents: MonitoredMediaEventType = addNameField({
  canplay: { code: 1 },
  canplaythrough: { code: 2 },
  complete: { code: 3 },
  durationchange: { code: 4, metadata: 'dur' },
  emptied: { code: 5 },
  ended: { code: 6 },
  loadeddata: { code: 7 },
  loadedmetadata: { code: 8 },
  pause: { code: 9, metadata: 'pos' },
  play: { code: 10, metadata: 'pos' },
  playing: { code: 11 },
  ratechange: { code: 12, metadata: 'rate' },
  seeked: { code: 13, metadata: 'pos' },
  seeking: { code: 14, metadata: 'pos' },
  stalled: { code: 15 },
  suspend: { code: 16 },
  waiting: { code: 17 },
  volumechange: { code: 18, metadata: 'mute' },
  resize: { code: 19, metadata: 'vr' }
});

export type ConstructedEventList =
  | 'audiochanged'
  | 'captionchanged'
  | 'playbackbitratechanged'
  | 'playbackstarted'
  | 'playerdispose'
  | 'resolutionchanged'; // Video Resolution Changed

// Events that are constructed, or gained from specific non video/window/document
// event listeners.
type ConstructedEventType = Readonly<
  Record<ConstructedEventList, Readonly<EventObject>>
>;
export const ConstructedEvents: ConstructedEventType = addNameField({
  playerdispose: { code: 102 }, // Microsoft event when AMP player is being removed
  playbackbitratechanged: { code: 103, metadata: 'b' }, // AMP player
  playbackstarted: { code: 106 }, // Constructed by ksdk and sent to stats
  audiochanged: { code: 110, metadata: 'audio' },
  captionchanged: { code: 111, metadata: 'caption' },
  resolutionchanged: { code: 112, metadata: 'cr' }
});

export type MonitoredWindowEventList =
  | 'beforeunload'
  | 'blur'
  | 'enterpictureinpicture'
  | 'focus'
  | 'fullscreenchange'
  | 'leavepictureinpicture'
  | 'pagehide'
  | 'visibilitychange';

export type MonitoredEventList =
  | ConstructedEventList
  | MonitoredMediaEventList
  | MonitoredWindowEventList;

type MonitoredWindowEventType = Readonly<
  Record<MonitoredWindowEventList, Readonly<EventObject>>
>;
export const MonitoredWindowEvents: MonitoredWindowEventType = addNameField({
  beforeunload: { code: 101, metadata: 'stop' },
  // 102, 103, and 106 are constructed
  visibilitychange: { code: 104, metadata: 'vis' },
  pagehide: { code: 105, metadata: 'stop' },
  // blur and focus are sent to the backend as a single focuschanged event
  blur: { code: 107, metadata: 'foc' },
  focus: { code: 107, metadata: 'foc' },
  fullscreenchange: { code: 108, metadata: 'full' },
  enterpictureinpicture: { code: 109, metadata: 'pip' },
  leavepictureinpicture: { code: 109, metadata: 'pip' }
});

export const AllMonitoredEvents: Readonly<EventObject[]> = Object.freeze([
  ...Object.values(ConstructedEvents),
  ...Object.values(MonitoredWindowEvents),
  ...Object.values(MonitoredMediaEvents)
]);

type RnEo = Record<number, EventObject>;
type RMelEo = Record<MonitoredEventList, EventObject>;

export const PLAYER_EVENTS_BY_CODE: Readonly<RnEo> = Object.freeze(
  AllMonitoredEvents.reduce((acc: RnEo, value) => {
    acc[value.code] = value;
    return acc;
  }, {} as RnEo)
);

export const PLAYER_EVENTS_BY_NAME: Readonly<RMelEo> = Object.freeze(
  AllMonitoredEvents.reduce((acc: RMelEo, value) => {
    acc[value.name] = value;
    return acc;
  }, {} as RMelEo)
);

export const StatefulPlayerEventNames: MonitoredEventList[] = [
  'audiochanged',
  'captionchanged',
  'durationchange',
  'enterpictureinpicture',
  'focus',
  'fullscreenchange',
  'playbackbitratechanged',
  'ratechange',
  'resize',
  'resolutionchanged',
  'visibilitychange',
  'volumechange'
];

export const StatefulPlayerEvents: Readonly<EventObject[]> = Object.freeze(
  AllMonitoredEvents.reduce((acc: EventObject[], value: EventObject) => {
    if (StatefulPlayerEventNames.indexOf(value.name) !== -1) {
      acc.push(value);
    }
    return acc;
  }, [])
);

export const EngagementEvents: number[] = [
  ConstructedEvents.audiochanged.code,
  ConstructedEvents.captionchanged.code,
  ConstructedEvents.resolutionchanged.code,
  MonitoredMediaEvents.resize.code,
  MonitoredMediaEvents.volumechange.code,
  MonitoredWindowEvents.blur.code,
  MonitoredWindowEvents.enterpictureinpicture.code,
  MonitoredWindowEvents.focus.code,
  MonitoredWindowEvents.fullscreenchange.code,
  MonitoredWindowEvents.leavepictureinpicture.code
];
// NOTE: visibilitychange is in a weird state, it isn't an EngagementEvent,
// because if it were we'd lose the ability to trigger sending the report on
// visibilityState: HIDDEN, so we have a function visibilityChangeHandler used
// in isPlayerEventHandled that makes it so we don't send it to the backend if
// EngagementEvents are disabled.

export const LIVE_DURATION = -1;
